/**
 * The block half of `components/markdown.tsx`: lines into a tree of blocks, with
 * no React in it. It lives apart so that `lib/task-list.ts` can find a checkbox
 * with the *same* parse that drew it — a toggle that counted `- [ ]` lines by a
 * regex of its own would disagree with the rendering the first time one sat in a
 * fenced block, and tick a line the user never saw as a checkbox.
 *
 * The subset is what an agent writing a status update actually uses: headings,
 * ordered and unordered lists (nested), task lists, fenced code, block quotes,
 * tables and rules. Inline syntax is the renderer's business.
 */

/** A list item's checkbox, and the line of the *original* source it sits on. */
export interface TaskMark {
  checked: boolean;
  line: number;
}

export type Block =
  | { type: "code"; lang: string; code: string }
  | { type: "heading"; level: number; text: string }
  | { type: "hr" }
  | { type: "quote"; children: Block[] }
  | { type: "list"; ordered: boolean; start: number; items: Block[][]; tasks: (TaskMark | null)[] }
  | { type: "table"; header: string[]; rows: string[][] }
  | { type: "para"; text: string };

const FENCE = /^ {0,3}(```+|~~~+)\s*([^\s`]*)/;
const HEADING = /^ {0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const HR = /^ {0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/;
const QUOTE = /^ {0,3}>\s?/;
const LIST_ITEM = /^(\s*)([-*+]|\d{1,9}[.)])(\s+)(.*)$/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;
/** `[ ]` / `[x]` opening a list item's first line, GFM's task-list marker. */
const TASK = /^\[([ xX])\](?:\s+|$)/;

const isBlank = (line: string) => line.trim() === "";

/** A pipe row split into cells, tolerating the optional leading/trailing pipe. */
function tableCells(line: string): string[] {
  let text = line.trim();
  if (text.startsWith("|")) text = text.slice(1);
  if (text.endsWith("|") && !text.endsWith("\\|")) text = text.slice(0, -1);
  return text.split(/(?<!\\)\|/).map((cell) => cell.trim().replace(/\\\|/g, "|"));
}

/** Source lines, split the way `parseBlocks` splits them (every line ending counts once). */
export function sourceLines(source: string): string[] {
  return source.replace(/\r\n?/g, "\n").split("\n");
}

/**
 * Lines into blocks. Lists and quotes recurse on their own content, which is what
 * makes a fenced code block inside a bullet — the shape "here is the diff I
 * applied, step 2" naturally takes — come out right.
 *
 * `origin` maps each line of `source` to its line in the text the user stored.
 * A nested list or quote is parsed from a copy of its lines with the marker or
 * indent stripped, one line for one line, so the map is carried down rather than
 * recomputed — it is what lets a checkbox three levels deep say which line it is.
 */
export function parseBlocks(source: string, origin?: number[]): Block[] {
  const lines = sourceLines(source);
  const at = origin ?? lines.map((_, index) => index);
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;

    if (isBlank(line)) {
      i += 1;
      continue;
    }

    const fence = line.match(FENCE);
    if (fence) {
      const marker = fence[1]!;
      const code: string[] = [];
      i += 1;
      // An unclosed fence runs to the end on purpose: a truncated comment should
      // still show its code as code rather than as prose full of backticks.
      while (i < lines.length && !new RegExp(`^ {0,3}${marker[0]}{${marker.length},}\\s*$`).test(lines[i]!)) {
        code.push(lines[i]!);
        i += 1;
      }
      i += 1;
      blocks.push({ type: "code", lang: fence[2] ?? "", code: code.join("\n") });
      continue;
    }

    if (HR.test(line)) {
      blocks.push({ type: "hr" });
      i += 1;
      continue;
    }

    const heading = line.match(HEADING);
    if (heading) {
      blocks.push({ type: "heading", level: heading[1]!.length, text: heading[2] ?? "" });
      i += 1;
      continue;
    }

    if (QUOTE.test(line)) {
      const quoted: string[] = [];
      const quotedAt: number[] = [];
      while (i < lines.length && (QUOTE.test(lines[i]!) || (!isBlank(lines[i]!) && quoted.length > 0))) {
        quoted.push(lines[i]!.replace(QUOTE, ""));
        quotedAt.push(at[i]!);
        i += 1;
      }
      blocks.push({ type: "quote", children: parseBlocks(quoted.join("\n"), quotedAt) });
      continue;
    }

    // A table needs its divider row to be a table at all, so both lines are
    // checked before either is consumed.
    if (line.includes("|") && i + 1 < lines.length && TABLE_DIVIDER.test(lines[i + 1]!) && lines[i + 1]!.includes("-")) {
      const header = tableCells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i]!.includes("|") && !isBlank(lines[i]!)) {
        rows.push(tableCells(lines[i]!));
        i += 1;
      }
      blocks.push({ type: "table", header, rows });
      continue;
    }

    const item = line.match(LIST_ITEM);
    if (item) {
      const ordered = /\d/.test(item[2]!);
      const start = ordered ? Number.parseInt(item[2]!, 10) : 1;
      const items: Block[][] = [];
      const tasks: (TaskMark | null)[] = [];
      // The list's items sit at its first item's indent. Not "at column 0": a child
      // list indented four spaces under a two-character marker reaches its own parse
      // still indented by two, and a loop that refused every indented item consumed
      // nothing and was handed the same line for ever — the whole app froze on an
      // email draft with such a list. The first item always matches, so this advances.
      const base = item[1]!.length;

      while (i < lines.length) {
        const current = lines[i]!.match(LIST_ITEM);
        // A different marker family starts a new list rather than continuing this
        // one, so a numbered plan following a bullet summary is not renumbered.
        if (!current || /\d/.test(current[2]!) !== ordered || current[1]!.length !== base) break;

        const indent = current[1]!.length + current[2]!.length + current[3]!.length;
        let first = current[4] ?? "";
        // The checkbox is the item's, not its text's: it comes off the first line
        // here so the paragraph under it renders without a literal `[ ]`.
        const task = first.match(TASK);
        tasks.push(task ? { checked: task[1] !== " ", line: at[i]! } : null);
        if (task) first = first.slice(task[0].length);
        const content = [first];
        const contentAt = [at[i]!];
        i += 1;

        // Continuation: anything indented under the marker belongs to this item,
        // and a blank line only ends it if what follows is not indented too.
        while (i < lines.length) {
          const next = lines[i]!;
          if (isBlank(next)) {
            const after = lines[i + 1];
            if (after === undefined || (!isBlank(after) && after.search(/\S/) < indent)) break;
            content.push("");
            contentAt.push(at[i]!);
            i += 1;
            continue;
          }
          if (next.search(/\S/) < indent) break;
          content.push(next.slice(indent));
          contentAt.push(at[i]!);
          i += 1;
        }
        items.push(parseBlocks(content.join("\n"), contentAt));
      }

      // Belt and braces: a branch that consumed nothing would loop for ever, so the
      // line goes out as text instead. Unreachable while `base` holds, and meant to stay so.
      if (items.length === 0) {
        blocks.push({ type: "para", text: line });
        i += 1;
        continue;
      }
      blocks.push({ type: "list", ordered, start, items, tasks });
      continue;
    }

    const paragraph: string[] = [];
    while (i < lines.length && !isBlank(lines[i]!)) {
      const next = lines[i]!;
      if (paragraph.length > 0 && (FENCE.test(next) || HEADING.test(next) || HR.test(next) || LIST_ITEM.test(next) || QUOTE.test(next))) {
        break;
      }
      paragraph.push(next);
      i += 1;
    }
    blocks.push({ type: "para", text: paragraph.join("\n") });
  }

  return blocks;
}

/** Every checkbox in a parsed tree, in the order they are drawn (which is source order). */
export function taskMarks(blocks: Block[]): TaskMark[] {
  const out: TaskMark[] = [];
  const walk = (list: Block[]) => {
    for (const block of list) {
      if (block.type === "quote") walk(block.children);
      if (block.type === "list") {
        block.items.forEach((item, index) => {
          const task = block.tasks[index];
          if (task) out.push(task);
          walk(item);
        });
      }
    }
  };
  walk(blocks);
  return out;
}
